import React, { useRef, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { Trash2, Upload, Image } from 'lucide-react';

/* ── FotosInstalacaoPanel ───────────────────────────────────────────────────
 * Props:
 *   leadId     – ID do lead para buscar/enviar ArquivoMultiplo
 *   readonly   – true = só visualizar (Pós Venda), false = pode editar
 *   initialOpen – painel já começa aberto (útil no modal do Kanban)
 * ─────────────────────────────────────────────────────────────────────────*/
export default function FotosInstalacaoPanel({ leadId, readonly = false, initialOpen = false }) {
  const [open, setOpen]           = useState(initialOpen);
  const [fotos, setFotos]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadErr, setLoadErr]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [draggingOver, setDraggingOver] = useState(false);
  const [lightbox, setLightbox]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef  = useRef(null);
  // ID único para o <label htmlFor> — stable por leadId
  const inputId  = `fotos-inst-${leadId}`;

  const load = async () => {
    if ((fotos !== null && !loadErr) || loading) return;
    setLoading(true); setLoadErr(false);
    try {
      const r = await api.get(`/documentos/arquivo-multiplo/list/?lead_id=${leadId}&categoria=foto_instalacao`);
      setFotos(r.data || []);
    } catch {
      setLoadErr(true);
    } finally { setLoading(false); }
  };

  // Carrega automaticamente ao abrir — cobre tanto o toggle normal quanto
  // o caso initialOpen=true (onde handleOpen nunca é chamado pelo usuário)
  useEffect(() => {
    if (open) load();
  }, [open]); // eslint-disable-line

  const handleOpen = () => setOpen(prev => !prev);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadErr('');
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('arquivo', file);
        fd.append('categoria', 'foto_instalacao');
        fd.append('lead_id', leadId);
        const r = await api.post('/documentos/arquivo-multiplo/upload/', fd);
        setFotos(prev => [...(prev || []), { id: r.data.id, url: r.data.url }]);
      } catch {
        setUploadErr('Erro ao enviar arquivo. Tente novamente.');
      }
    }
    setUploading(false);
  };

  /* ── Delete ──────────────────────────────────────────────────────────── */
  const handleDeleteRequest = (id) => setDeleteTarget(id);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/documentos/arquivo-multiplo/${id}/delete/`);
      setFotos(prev => prev.filter(f => f.id !== id));
    } catch {
      setUploadErr('Erro ao remover foto.');
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDraggingOver(false);
    if (readonly) return;
    uploadFiles(e.dataTransfer.files);
  }, [readonly, leadId]); // eslint-disable-line

  const isVideo = (url) => url && /\.(mp4|mov|avi|webm|mkv)$/i.test(url);
  const count    = fotos?.length || 0;
  const minFotos = 4;

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid #1a2035', paddingTop: 16 }}>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 9, cursor: 'pointer',
          background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.25)',
          color: '#a78bfa', fontSize: 13, fontWeight: 700,
        }}
      >
        <span>📸 Fotos da Instalação {count > 0 ? `(${count})` : ''}</span>
        <span style={{ fontSize: 11 }}>{open ? '▲ Fechar' : '▼ Abrir'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10, background: '#080c14', border: '1px solid #1a2035', borderRadius: 10, padding: '12px 16px' }}>
          {loading && <div style={{ fontSize: 13, color: '#475569', textAlign: 'center', padding: 20 }}>Carregando...</div>}
          {!loading && loadErr && (
            <div style={{ fontSize: 13, color: '#f87171', textAlign: 'center', padding: 12 }}>
              Erro ao carregar.{' '}
              <button onClick={() => { setFotos(null); load(); }} style={{ color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                Tentar novamente
              </button>
            </div>
          )}
          {!loading && fotos !== null && (
            <>
              {/* Aviso de mínimo */}
              {count < minFotos && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)',
                  borderRadius: 8, padding: '8px 12px', marginBottom: 12,
                  fontSize: 12, color: '#fbbf24',
                }}>
                  📌 Recomendado: mínimo {minFotos} fotos. Você tem {count}.
                </div>
              )}

              {/* Confirmação inline de remoção (substitui window.confirm) */}
              {deleteTarget && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
                  borderRadius: 7, padding: '8px 12px', marginBottom: 10,
                }}>
                  <span style={{ fontSize: 13, color: '#f87171' }}>Remover esta foto?</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setDeleteTarget(null)}
                      style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 6, padding: '5px 12px', color: '#64748b', fontSize: 12, cursor: 'pointer' }}
                    >Cancelar</button>
                    <button
                      onClick={handleDeleteConfirm}
                      style={{ background: 'rgba(248,113,113,0.2)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: 6, padding: '5px 12px', color: '#f87171', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >Remover</button>
                  </div>
                </div>
              )}

              {/* Grade de fotos */}
              {count > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                  {fotos.map(f => (
                    <div key={f.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#1a2035', aspectRatio: '4/3' }}>
                      {isVideo(f.url) ? (
                        <video
                          src={f.url}
                          controls
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <img
                          src={f.url}
                          alt="Foto instalação"
                          onClick={() => setLightbox(f.url)}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                        />
                      )}
                      {!readonly && (
                        <button
                          onClick={() => handleDeleteRequest(f.id)}
                          style={{
                            position: 'absolute', top: 4, right: 4,
                            background: 'rgba(248,113,113,0.85)', border: 'none',
                            borderRadius: 6, padding: '4px 6px', cursor: 'pointer',
                            color: '#fff', display: 'flex', alignItems: 'center',
                          }}
                          title="Remover foto"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {count === 0 && (
                <div style={{ fontSize: 13, color: '#475569', textAlign: 'center', padding: '12px 0', marginBottom: 8 }}>
                  <Image size={28} style={{ marginBottom: 6, opacity: 0.4 }} />
                  <div>Nenhuma foto adicionada ainda.</div>
                </div>
              )}

              {/* Mensagem de erro de upload inline */}
              {uploadErr && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 12, color: '#f87171', marginBottom: 8 }}>
                  <span>{uploadErr}</span>
                  <button onClick={() => setUploadErr('')} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                </div>
              )}

              {/* Upload — só para não-readonly */}
              {!readonly && (
                <>
                  {/*
                    label htmlFor → ativa o input nativamente no browser/iOS/Android/PWA.
                    Mais confiável que div + fileRef.click() em iOS Safari standalone mode.
                    disabled={uploading} no input impede a abertura do seletor durante upload.
                  */}
                  <label
                    htmlFor={inputId}
                    onDragOver={e => { e.preventDefault(); setDraggingOver(true); }}
                    onDragLeave={() => setDraggingOver(false)}
                    onDrop={onDrop}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                      border: `2px dashed ${draggingOver ? '#a78bfa' : '#2d3748'}`,
                      borderRadius: 9, padding: '18px 12px', textAlign: 'center',
                      cursor: uploading ? 'wait' : 'pointer',
                      background: draggingOver ? 'rgba(167,139,250,0.08)' : 'transparent',
                      transition: 'all 0.15s',
                      minHeight: 56,
                      userSelect: 'none',
                    }}
                  >
                    <Upload size={18} style={{ color: '#a78bfa', pointerEvents: 'none' }} />
                    <div style={{ fontSize: 12, color: '#64748b', pointerEvents: 'none' }}>
                      {uploading ? 'Enviando...' : 'Clique ou arraste fotos / vídeos aqui'}
                    </div>
                  </label>
                  <input
                    id={inputId}
                    ref={fileRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    disabled={uploading}
                    style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
                    onChange={e => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ''; }}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99999, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );
}
